import React, { useEffect, useRef, useState } from 'react'

/**
 * InvoicePreview — right-panel of Step 3. Stripe-style invoice document.
 * 1:1 port of Scale Portal's HourlyInvoice/InvoicePreview.jsx.
 *
 * Sections:
 *   1. Side-toggle (Plaintiff/Defendant buttons; chevron when >1 party per side)
 *   2. Blue info bar "Showing X portion (Y% of total)" — only when not 'one'
 *   3. Invoice header: <h3>Invoice</h3>, invoice number / date of issue / date due (left), logo (right)
 *   4. From / Bill To (2-col)
 *   5. <hr/>
 *   6. Amount Due banner: $X.XX USD due <date> + case name + (#case_number) - mediation date
 *   7. <hr/>
 *   8. Line items table: Description / Quantity / Price / Amount
 *      - "(HH:MM hours)" appended inline to description
 *      - SplitInvoicePerParty row when multi-party
 *      - Processing fee row when on
 *      - Admin fee row when in_line_item
 *   9. Totals box (right-aligned w-64): Subtotal / Total / Amount Due (with USD)
 *  10. Footer: invoice_footer / cancellation_policy / additional_information
 */

function fmtMoney(n) {
    const v = Number(n) || 0
    return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

// Scale parity: format any phone (digits or formatted) into "+1 XXX-XXX-XXXX"
// for invoice display. Accepts 10-digit US, 11-digit US (with leading 1),
// or returns the input unchanged if it doesn't match.
function fmtInvoicePhone(phone) {
    if (!phone) return ''
    try {
        const cleaned = String(phone).replace(/\D/g, '')
        if (cleaned.length === 11 && cleaned[0] === '1') {
            const m = cleaned.match(/^1(\d{3})(\d{3})(\d{4})$/)
            if (m) return `+1 ${m[1]}-${m[2]}-${m[3]}`
        }
        if (cleaned.length === 10) {
            const m = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/)
            if (m) return `+1 ${m[1]}-${m[2]}-${m[3]}`
        }
        return phone
    } catch (_e) { return phone }
}

function fmtDate(s) {
    if (!s) return '—'
    try {
        const m = typeof s === 'string' && /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10).split('-') : null
        const d = m ? new Date(Number(m[0]), Number(m[1]) - 1, Number(m[2])) : new Date(s)
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    } catch (_e) { return s }
}

function decimalToHHMM(decimal) {
    const v = Number(decimal) || 0
    const h = Math.floor(v)
    const m = Math.round((v - h) * 60)
    return `${h}:${String(m).padStart(2, '0')}`
}

const ChevronDown = ({ className = '' }) => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" className={className}>
        <path d="M5 7l5 6 5-6H5z"/>
    </svg>
)

const ForkIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="6" cy="3" r="3"/>
        <circle cx="6" cy="21" r="3"/>
        <circle cx="18" cy="12" r="3"/>
        <path d="M6 6v9"/><path d="M6 18v-3"/><path d="M9 12h6"/>
    </svg>
)

const CheckIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12"/>
    </svg>
)

export default function InvoicePreview({
    values,
    mediator,
    caseData,
    plaintiffOptions = [],
    defendantOptions = [],
    initialClient,
    selectClientById,
    splitFactor = 1,
    perPartyShare = 0,
    // Optional: { [client_id]: exactShare }, cent-exact per party (e.g. from
    // splitWhole). Preferred over the flat `perPartyShare` average whenever
    // the currently-toggled party has an entry — a 2-way split of an
    // odd-cent subtotal gives the two parties shares 1c apart, and this is
    // what keeps the preview showing the SAME number that is actually sent
    // in that party's payload, not a rounded average of both.
    perPartySharesByClientId = null,
    perPartyPercentage = 100,
    processingFeeToClient = false,
    adminFee = 0,
    // Preferred over `adminFee`: already resolved per party by useAdminFee.
    adminFeePerParty = null,
    isAdminFeeAddedInLineItem = false,
}) {
    const items = values.items || []
    const [selected, setSelected] = useState(initialClient || null)
    const [isPlaintiffOpen, setIsPlaintiffOpen] = useState(false)
    const [isDefendantOpen, setIsDefendantOpen] = useState(false)
    const plaintiffRef = useRef(null)
    const defendantRef = useRef(null)

    useEffect(() => { if (initialClient) setSelected(initialClient) }, [initialClient?.client_id])

    useEffect(() => {
        const handle = (e) => {
            if (plaintiffRef.current && !plaintiffRef.current.contains(e.target)) setIsPlaintiffOpen(false)
            if (defendantRef.current && !defendantRef.current.contains(e.target)) setIsDefendantOpen(false)
        }
        document.addEventListener('mousedown', handle)
        return () => document.removeEventListener('mousedown', handle)
    }, [])

    const isOne = values.selectedOption === 'one'
    const showSideToggle = !isOne && plaintiffOptions.length > 0 && defendantOptions.length > 0

    const pickClient = (id) => {
        if (typeof selectClientById === 'function') {
            const next = selectClientById(id)
            if (next) setSelected(next)
        }
        setIsPlaintiffOpen(false)
        setIsDefendantOpen(false)
    }

    const selectedRoleIsPlaintiff = (selected?.role || '').toLowerCase().includes('plaintiff')
    const selectedRoleIsDefendant = (selected?.role || '').toLowerCase().includes('defendant')

    // Prefer the cent-exact per-party figure for whichever party is currently
    // toggled; fall back to the flat average when the caller didn't supply a
    // map (the hourly flow) or the toggled party has no entry in it yet.
    const resolvedPerPartyShare =
        perPartySharesByClientId != null
        && selected?.client_id != null
        && perPartySharesByClientId[selected.client_id] != null
            ? Number(perPartySharesByClientId[selected.client_id]) || 0
            : (perPartyShare || 0)

    // Totals — Scale uses displayTotal (per-party share + per-party admin fee + processing fee)
    // plus the line-item admin fee (when not added) on top.
    const adminFeeLine = isAdminFeeAddedInLineItem ? Number(adminFee) || 0 : 0
    // Per-party admin fee comes from useAdminFee, which knows whether the
    // mediator's fee is FLAT (charged to every party in full) or a PERCENTAGE
    // of the case subtotal (shared between them). Dividing here regardless was
    // what showed $125 against the portal's $250.
    const perPartyAdminFee = adminFeePerParty != null
        ? Number(adminFeePerParty) || 0
        : (splitFactor > 0 ? (Number(adminFee) || 0) / splitFactor : 0)
    const perPartyProcFee = processingFeeToClient
        ? Math.floor((resolvedPerPartyShare * 0.022 + 0.3) * 100) / 100
        : 0
    const displayTotal = resolvedPerPartyShare + (isAdminFeeAddedInLineItem ? perPartyAdminFee : 0) + perPartyProcFee
    const grandTotal = displayTotal + 0 // Subtotal/Total/Amount Due all show this on Scale

    const businessName = mediator?.business_name || `${mediator?.first_name || ''} ${mediator?.last_name || ''}`.trim()
    // Scale uses `stripe_session` (singular) on the mediator record. We
    // accept both shapes so a renamed BE field doesn't break this view.
    const businessAddress =
        mediator?.stripe_session?.business_address
        || mediator?.stripe_sessions?.business_address
        || mediator?.stripeAddress
        || null
    const stripeLogo = mediator?.stripeLogo || mediator?.stripe_logo || mediator?.photo_url || null

    const caseFullName = caseData?.case_name
        + (Array.isArray(caseData?.additional_case_names) && caseData.additional_case_names.length
            ? `, ${caseData.additional_case_names.join(', ')}`
            : '')

    return (
        // Default body typography: Inter (no Inter Tight), gray-500 (#6B7280),
        // 16px. Headings/strong elements override their own size + color
        // (text-2xl text-gray-800 on h3, text-lg on h5, font-bold text-black
        // on names, text-[14px] on table rows, etc.) so this only affects
        // unstyled body spans — matches Scale's default render.
        <div className="font-inter text-[16px] text-gray-500 bg-white rounded-lg border border-gray-200 p-5">
            {/* Section 1: side toggle */}
            {showSideToggle && (
                <div className="flex justify-between gap-2">
                    {/* Plaintiff side */}
                    <div className={`relative ${defendantOptions.length > 0 ? 'w-1/2' : 'w-full'}`} ref={plaintiffRef}>
                        {plaintiffOptions.length === 1 && defendantOptions.length === 1 ? (
                            <button
                                type="button"
                                onClick={() => pickClient(plaintiffOptions[0].value)}
                                className={`w-full px-4 py-2 mb-3 rounded-md text-sm font-medium border shadow-md ${selectedRoleIsPlaintiff ? 'bg-teal-800 text-white' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'}`}
                            >
                                <div className="w-full flex justify-center">
                                    <span>{plaintiffOptions[0].label}</span>
                                </div>
                            </button>
                        ) : (
                            <>
                                <button
                                    type="button"
                                    onClick={() => setIsPlaintiffOpen((p) => !p)}
                                    className={`w-full px-4 py-2 mb-3 rounded-md text-sm font-medium border shadow-md ${selectedRoleIsPlaintiff ? 'bg-teal-800 text-white' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'}`}
                                >
                                    <div className="flex justify-between items-center">
                                        <div className="w-full flex justify-center"><span>Plaintiff</span></div>
                                        <ChevronDown className={`transition-transform duration-200 ${isPlaintiffOpen ? 'rotate-180' : ''}`} />
                                    </div>
                                </button>
                                {isPlaintiffOpen && (
                                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-50">
                                        {plaintiffOptions.map((opt) => (
                                            <button
                                                type="button"
                                                key={opt.value}
                                                onClick={() => pickClient(opt.value)}
                                                className="capitalize text-gray-500 w-full text-left px-4 py-2 hover:bg-gray-100 first:rounded-t-md last:rounded-b-md focus:outline-none"
                                            >
                                                <div className="flex justify-between items-center">
                                                    <span>{opt.label}</span>
                                                    {selected?.client_id === opt.value && <CheckIcon />}
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Defendant side */}
                    <div className={`relative ${plaintiffOptions.length > 0 ? 'w-1/2' : 'w-full'}`} ref={defendantRef}>
                        {plaintiffOptions.length === 1 && defendantOptions.length === 1 ? (
                            <button
                                type="button"
                                onClick={() => pickClient(defendantOptions[0].value)}
                                className={`w-full px-4 py-2 mb-3 rounded-md text-sm font-medium border shadow-md ${selectedRoleIsDefendant ? 'bg-teal-800 text-white' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'}`}
                            >
                                <div className="w-full flex justify-center">
                                    <span>{defendantOptions[0].label}</span>
                                </div>
                            </button>
                        ) : (
                            <>
                                <button
                                    type="button"
                                    onClick={() => setIsDefendantOpen((p) => !p)}
                                    className={`w-full px-4 py-2 mb-3 rounded-md text-sm font-medium border shadow-md ${selectedRoleIsDefendant ? 'bg-teal-800 text-white' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'}`}
                                >
                                    <div className="flex justify-between items-center">
                                        <div className="w-full flex justify-center"><span>Defendant</span></div>
                                        <ChevronDown className={`transition-transform duration-200 ${isDefendantOpen ? 'rotate-180' : ''}`} />
                                    </div>
                                </button>
                                {isDefendantOpen && (
                                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-50">
                                        {defendantOptions.map((opt) => (
                                            <button
                                                type="button"
                                                key={opt.value}
                                                onClick={() => pickClient(opt.value)}
                                                className="capitalize text-gray-500 w-full text-left px-4 py-2 hover:bg-gray-100 first:rounded-t-md last:rounded-b-md focus:outline-none"
                                            >
                                                <div className="flex justify-between items-center">
                                                    <span>{opt.label}</span>
                                                    {selected?.client_id === opt.value && <CheckIcon />}
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Section 2: blue info bar */}
            {!isOne && (
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                    <div className="flex justify-between items-start">
                        <div className="flex flex-col">
                            <div className="flex items-center gap-2 mt-2 text-blue-500">
                                <ForkIcon />
                                <p className="font-medium">
                                    Showing {selected?.name || '—'} portion ({perPartyPercentage}% of total)
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Section 3: Invoice header — Scale uses raw <h3> styling and
                wraps inner content in an extra <div>. */}
            <div className="flex flex-col md:flex-row justify-between gap-4 py-5">
                <div>
                    <div>
                        <h3 className="text-2xl font-bold text-gray-800">Invoice</h3>
                        <br />
                        <span>Invoice number: {values.invoice_number || '—'}</span>
                        <br />
                        <span>Date of issue: {fmtDate(values.date || new Date().toISOString())}</span>
                        <br />
                        <span>Date due: {fmtDate(values.due_date)}</span>
                    </div>
                </div>
                <div>
                    {stripeLogo ? (
                        <img src={stripeLogo} alt="logo" className="max-w-[170px]" />
                    ) : null}
                </div>
            </div>

            {/* Section 4: From / Bill To */}
            <div className="flex flex-col md:flex-row gap-4 py-5 mb-5">
                <div className="w-full md:w-1/2">
                    <div className="text-[16px]">
                        <span className="font-bold text-black">{businessName || '—'}</span>
                        <br />
                        {businessAddress?.line1 ? <><span>{businessAddress.line1}</span><br /></> : null}
                        {businessAddress?.line2 ? <><span>{businessAddress.line2}</span><br /></> : null}
                        {businessAddress?.city ? (
                            <>
                                <span>{businessAddress.city}{businessAddress.state ? `, ${businessAddress.state}` : ''} {businessAddress.postal_code || ''}</span>
                                <br />
                            </>
                        ) : null}
                        {businessAddress?.country ? <><span>{businessAddress.country}</span><br /></> : null}
                        {!businessAddress && mediator?.stripeAddress ? <><span>{mediator.stripeAddress}</span><br /></> : null}
                        {mediator?.phone ? <><br /><span>{fmtInvoicePhone(mediator.phone)}</span></> : null}
                        {mediator?.email ? <><br /><span>{mediator.email}</span></> : null}
                    </div>
                </div>
                <div className="w-full md:w-1/2">
                    <div className="text-[16px]">
                        <span className="font-bold text-black">Bill To</span>
                        <br />
                        {selected ? (
                            <>
                                <span>{selected.name}</span>
                                {selected.address ? <><br /><span>{selected.address}</span></> : null}
                                {selected.email ? <><br /><span>Email: {selected.email}</span></> : null}
                                {(selected.phone || selected.phone_number) ? <><br /><span>Phone: {fmtInvoicePhone(selected.phone || selected.phone_number)}</span></> : null}
                            </>
                        ) : <span>No client selected</span>}
                    </div>
                </div>
            </div>

            <hr />

            {/* Section 6: Amount Due banner — Scale uses raw <h5>+<p> styling.
                Append " - {first} {last}" to the case description when the
                mediator's `add_mediator_name_in_description` setting is true
                (Scale parity). */}
            <div className="flex flex-col gap-1 py-10">
                <h5 className="text-lg font-bold text-gray-800">${fmtMoney(grandTotal)}USD due {fmtDate(values.due_date)}</h5>
                <p>
                    {caseFullName || '—'}
                    {caseData?.case_number ? ` (#${caseData.case_number})` : ''}
                    {` - ${caseData?.mediation_date ? fmtDate(caseData.mediation_date) : '—'}`}
                    {(mediator?.mediatorSettings?.settings?.add_mediator_name_in_description === true
                        || mediator?.add_mediator_name_in_description === true)
                        && mediator?.first_name && mediator?.last_name
                        ? ` - ${mediator.first_name} ${mediator.last_name}`
                        : ''}
                </p>
            </div>

            <hr />

            {/* Section 8: Line items table */}
            <table className="w-full">
                <thead>
                    <tr>
                        <th className="py-2 px-4 text-left text-gray-700">Description</th>
                        <th className="py-2 px-4 text-right text-gray-700 w-24">Quantity</th>
                        <th className="py-2 px-4 text-right text-gray-700 w-32">Price</th>
                        <th className="py-2 px-4 text-right text-gray-700 w-32">Amount</th>
                    </tr>
                    <tr>
                        <td colSpan={4} className="p-0"><hr className="border-t border-gray-300" /></td>
                    </tr>
                </thead>
                <tbody>
                    {/* `_isCredit` marks a row that is NOT hourly work — the
                        per-party share credit the hourly EDIT wizard appends
                        (UpdateInvoice/HourlyUpdateInvoice/previewAdapter.js).
                        Formatted like a line item it reads as one hour of work
                        at a negative hourly rate, so it is drawn the way the
                        built-in credit row below is drawn: no hours suffix, no
                        unit price, amount as "- $600.00". Gated on the FLAG and
                        not on `amount < 0`, so the create flow is unaffected. */}
                    {items.map((it, i) => (
                        <tr key={it.id || i} className="border-b border-gray-200">
                            <td className="py-3 px-4">
                                <span className="font-semibold text-[14px]">{it.description || '-'}</span>
                                {!it._isCredit && (
                                    <span className="ml-1 font-semibold text-[14px]">({decimalToHHMM(it.quantity)} hours)</span>
                                )}
                            </td>
                            <td className="py-3 px-4 text-center">1</td>
                            <td className="py-3 px-4 text-right font-semibold text-[14px]">
                                {it._isCredit ? '' : `$${fmtMoney(it.rate)}`}
                            </td>
                            <td className="py-3 px-4 text-right font-semibold text-[14px]">
                                {it._isCredit
                                    ? `- $${fmtMoney(Math.abs(Number(it.amount) || 0))}`
                                    : `$${fmtMoney(it.amount)}`}
                            </td>
                        </tr>
                    ))}
                    {/* "TOTAL DUE BY EACH PARTY" only means something when the
                        case total is actually being DIVIDED. At 100% there is no
                        split to describe, and the row is worse than redundant:
                        it is drawn as a CREDIT ("- $320"), so on a fixed "both"
                        edit — where each invoice carries the whole new total —
                        it reads as if the party owed nothing.

                        The mediator portal reaches the same place from the other
                        direction: its equivalent row is suppressed outright in
                        edit mode (FixedUpdateInvoice/common/InvoicePreview.jsx,
                        `!isEditMode && ...`). Keying on the percentage rather
                        than the mode keeps the hourly create flow — where a real
                        split still needs explaining — untouched. */}
                    {!isOne && Number(perPartyPercentage) < 100 && (
                        <tr className="border-b border-gray-200">
                            <td className="py-3 px-4 font-semibold">
                                TOTAL DUE BY EACH PARTY (~{perPartyPercentage}%)
                            </td>
                            <td className="py-3 px-4 text-center">1</td>
                            <td className="py-3 px-4 text-right"></td>
                            <td className="py-3 px-4 text-right font-semibold text-[14px]">
                                - ${fmtMoney(resolvedPerPartyShare)}
                            </td>
                        </tr>
                    )}
                    {processingFeeToClient && (
                        <tr className="border-b border-gray-200">
                            <td className="py-3 px-4 font-semibold">Credit Card Processing Fee</td>
                            <td className="py-3 px-4 text-center">1</td>
                            <td className="py-3 px-4 text-right"></td>
                            <td className="py-3 px-4 text-right font-semibold text-[14px]">${fmtMoney(perPartyProcFee)}</td>
                        </tr>
                    )}
                    {isAdminFeeAddedInLineItem && (
                        <tr className="border-b border-gray-200">
                            <td className="py-3 px-4 font-semibold">Scale Mediation Admin Fee</td>
                            <td className="py-3 px-4 text-center">1</td>
                            <td className="py-3 px-4 text-right"></td>
                            {/* Admin fee renders as a raw number (no .toFixed) to match Scale's
                                "$50" display rather than "$50.00". */}
                            <td className="py-3 px-4 text-right font-semibold text-[13px]">${Number(perPartyAdminFee) || 0}</td>
                        </tr>
                    )}
                </tbody>
            </table>

            {/* Section 9: Totals box — Scale parity (InvoicePreview.jsx:825–870):
                Subtotal / Total / Amount Due all show the SAME per-party grand
                total. Subtotal is NOT the raw line-items sum here — it's the
                portion this specific party owes after split + fees. */}
            <div className="p-4">
                <div className="flex justify-end">
                    <div className="w-64">
                        <div className="grid grid-cols-2 gap-2 mb-2">
                            <p className="font-bold text-[14px]">Subtotal:</p>
                            <p className="text-right font-bold text-[14px]">${fmtMoney(grandTotal)}</p>
                        </div>
                        <div className="border-t border-gray-300 pt-2 mt-2">
                            <div className="grid grid-cols-2">
                                <p className="font-bold text-lg">Total:</p>
                                <p className="text-right font-bold text-lg">${fmtMoney(grandTotal)}</p>
                            </div>
                        </div>
                        <div className="border-t border-gray-300 pt-2 mt-2">
                            <div className="grid grid-cols-2">
                                <p className="font-bold text-lg">Amount Due:</p>
                                <p className="text-right font-bold text-lg">${fmtMoney(grandTotal)}USD</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Section 10: Footer — Scale always renders this block. */}
            <div className="p-6 border-t whitespace-pre-line">
                {`${mediator?.invoice_footer || ''}
                ${mediator?.cancellation_policy ? `Cancellation Policy: ${mediator.cancellation_policy}` : ''}
                ${mediator?.additional_information ? `Additional Information: ${mediator.additional_information}` : ''}`}
            </div>
        </div>
    )
}
